import { FC, useEffect, useState } from 'react';

import { Form, Formik } from 'formik';
import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';

import FormikInput from 'components/primitives/FormikInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { Pickup } from 'types/pickup';
import { CollectionPoint } from 'types/location';

import { getInitialValues, validationSchema } from '../EditPickupModal.utils';
import FormikSwitch from 'components/primitives/FormikSwitch';
import { Site, SiteTypeEnum } from 'types/site';

type Props = {
  pickup: Pickup;
  isUpdating: boolean;
  onCancel: () => void;
  collectionPoints: CollectionPoint[];
  sites: Site[] | undefined;
  onUpdate: (values: Pickup) => void;
};
const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

export const EditForm: FC<Props> = ({
  pickup,
  onCancel,
  onUpdate,
  isUpdating,
  collectionPoints,
  sites,
}) => {
  const initialValues = getInitialValues(pickup) as any;
  const [showToggle, setShowToggle] = useState(true);

  useEffect(() => {
    const cp = collectionPoints?.find(
      (c) => c._id === initialValues.collectionPoint,
    );
    const site = sites?.find((s) => s._id === cp?.site);

    setShowToggle(site?.siteType !== SiteTypeEnum.THIRD_PARTY_WASH);
  }, [initialValues.collectionPoint, collectionPoints, sites]);

  const handleCollectionPointChange = (value: string, setFieldValue: any) => {
    const cp = collectionPoints?.find((c) => c._id === value);
    const site = sites?.find((s) => s._id === cp?.site);

    if (site?.siteType === SiteTypeEnum.THIRD_PARTY_WASH) {
      setShowToggle(false);
      setFieldValue('isFinalPickup', false);
    } else {
      setShowToggle(true);
      setFieldValue('isFinalPickup', initialValues.isFinalPickup);
    }
  };
  return (
    <>
      <Flex
        shadow="lg"
        w="100%"
        py="30px"
        align="center"
        justify="space-between"
        px={{ base: '20px', md: '100px' }}>
        <Heading fontSize="24px" fontWeight="600">
          Pickup Details
        </Heading>
      </Flex>
      <Flex direction="column" p={6} minH="200px">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onUpdate}>
          {({ isValid, setFieldValue }) => (
            <Form>
              <Flex>
                <FormikSelectInput
                  onChange={(value) =>
                    handleCollectionPointChange(value, setFieldValue)
                  }
                  placeholder="Select Collection Point"
                  label="Collection Point (*)"
                  name="collectionPoint">
                  {collectionPoints?.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s?.name || ''}
                    </option>
                  ))}
                </FormikSelectInput>
                <Box h="20px" w="50px" />
                <FormikDatepicker
                  name="date"
                  label="Pickup Date (*)"
                  labelProps={labelProps}
                  formatInUTC
                  minDate={null}
                />
              </Flex>
              <FormikInput
                containerProps={{ mt: 4 }}
                name="eventName"
                label="Event Name"
                labelProps={labelProps}
              />
              <FormikInput
                containerProps={{ mt: 4 }}
                name="notes"
                label="Notes"
                labelProps={labelProps}
              />
              {showToggle && (
                <Flex flex={1} alignItems="center" marginTop={4}>
                  <FormikSwitch name="isFinalPickup" />
                  <Flex flexDir="column" ml={4}>
                    <Text>Final Pickup</Text>
                    <Text fontSize="xs">
                      For events or sites collecting unused cases & other
                      equipment.
                    </Text>
                  </Flex>
                </Flex>
              )}
              <Flex mt={12} flexDirection="row" justifyContent="center">
                <Button
                  marginRight="10px"
                  isDisabled={isUpdating}
                  w="134px"
                  onClick={onCancel}
                  variant="outline">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isUpdating}
                  isDisabled={!isValid}
                  w="134px">
                  Update
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </>
  );
};
